import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Wand2,
  Briefcase,
  GraduationCap,
  FileText,
  Paperclip,
  ChevronRight,
  CheckCircle2,
  Building2,
  Upload,
} from 'lucide-react';

import './JobPosting.css';
import JobPostingPreview from './JobPostingPreview';
import CollegeSelection from './CollegeSelection';

// --- CONSTANTS ---
const roleOptions = [
  { value: 'Front Office', label: 'Front Office (guest check-in)' },
  { value: 'Housekeeping', label: 'Housekeeping (cleaning)' },
  { value: 'F&B Service', label: 'Food & Beverage Service/Production' },
  { value: 'Sales & Marketing', label: 'Sales & Marketing' },
  { value: 'Engineering', label: 'Engineering (maintenance)' },
  { value: 'Security', label: 'Security' },
  { value: 'Human Resources', label: 'Human Resources' },
];

const workTypes = [
  { value: 'Full-time', label: 'Full-time' },
  { value: 'Internship', label: 'Internship' },
  { value: 'Casual', label: 'Casual' },
];

const graduationDegrees = [
  { value: 'Diploma', label: 'Diploma' },
  { value: 'Bachelor Hospitality', label: 'Bachelor Degree in Hospitality Management' },
  { value: 'BHM', label: 'Bachelor of Hotel Management (BHM)' },
  { value: 'B.Sc Hospitality', label: 'B.Sc. in Hospitality and Hotel Administration' },
  { value: 'BBA Tourism', label: 'BBA in Tourism/Hospitality' },
  { value: 'Any', label: 'Any Graduation' },
];

const genders = [
  { value: 'Any', label: 'Any' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const initialFormData = {
  companyName: '',
  role: null,
  batch: '',
  location: '',
  typeOfWork: null,
  ctc: '',
  applyByDate: '',
  applyByTime: '',
  dateOfVisit: '',
  tenth: '',
  twelfth: '',
  graduation: [],
  minCGPA: '',
  gender: null,
  targetCountry: null,
  aboutCompany: '',
  aboutJob: '',
  hiringWorkflows: '',
  externalLink: '',
  additionalInfo: '',
  selectedJd: null,
  otherFiles: [],
};

const JobPosting = ({ onPostSuccess, providedId }) => {
  const isCustomer = !!providedId || !!localStorage.getItem('customerId');

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  
  // Local state for the Multi-Select Location objects
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const [savedJDs, setSavedJDs] = useState([]);
  const [selectedSavedJD, setSelectedSavedJD] = useState(null);

  const [enableCriteria, setEnableCriteria] = useState({
    tenth: false,
    twelfth: false,
    minCGPA: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = query.get('id');
  const isEdit = !!editId;

  // 🚨 LIVE API 1: OPEN-METEO FOR CITIES 🚨
  const loadCityOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const res = await axios.get(`https://geocoding-api.open-meteo.com/v1/search?name=${inputValue}&count=10&language=en&format=json`);
      if (!res.data.results) return [];
      
      return res.data.results.map(city => ({
        label: `${city.name}, ${city.country}`,
        value: `${city.name}, ${city.country}`,
        country: city.country // Used for the lockdown logic
      }));
    } catch (e) {
      console.error("City API Error", e);
      return [];
    }
  };

  // 🚨 FIXED: Fetch Live Countries ONCE on load from a completely different API! 🚨
  useEffect(() => {
    const fetchLiveCountries = async () => {
      try {
        // Bypassing restcountries.com entirely due to their strict rate limits
        const res = await axios.get('https://countriesnow.space/api/v0.1/countries/iso');
        
        // The data structure is slightly different: res.data.data contains the array
        const uniqueCountries = [...new Set(res.data.data.map(c => c.name))].sort();
        
        setCountryOptions(uniqueCountries.map(country => ({
          label: country,
          value: country
        })));
      } catch (e) {
        console.error("Live Country API Error", e);
      }
    };
    fetchLiveCountries();
  }, []);

  useEffect(() => {
    const fetchSavedJDs = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-description`);
        const storedId =
          providedId ||
          localStorage.getItem('userId') ||
          localStorage.getItem('recruiterId') ||
          localStorage.getItem('customerId');

        const myJDs = res.data.filter((jd) => jd.creatorId === storedId);
        setSavedJDs(myJDs);
      } catch (error) {
        console.error('Failed to fetch Saved JDs', error);
      }
    };
    fetchSavedJDs();
  }, [providedId]);

  useEffect(() => {
    const fetchJobPostForEdit = async () => {
      if (isEdit) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${editId}`
          );
          const post = response.data;

          // Parse the location string back into objects for the Multi-Select
          let parsedLocations = [];
          if (post.location) {
              parsedLocations = post.location.split(' | ').map(loc => {
                  const parts = loc.split(', ');
                  return {
                      label: loc,
                      value: loc,
                      country: parts.length > 1 ? parts[parts.length - 1] : null,
                      __isNew__: parts.length === 1 // Treat "Remote" as custom
                  };
              });
          }
          setSelectedLocations(parsedLocations);

          setFormData({
            ...initialFormData,
            ...post,
            otherFiles: post.otherFiles || [],
            targetColleges: post.targetColleges || [],
            typeOfWork: workTypes.find((t) => t.value === post.typeOfWork) || null,
            gender: genders.find((g) => g.value === post.gender) || null,
            targetCountry: post.targetCountry ? { value: post.targetCountry.trim(), label: post.targetCountry.trim() } : null,
            role:
              roleOptions.find((r) => r.value === post.role) ||
              { value: post.role, label: post.role },
            graduation: post.graduation
              ? post.graduation.map(
                  (g) => graduationDegrees.find((d) => d.value === g) || { value: g, label: g }
                )
              : [],
          });

          setEnableCriteria({
            tenth: !!post.tenth,
            twelfth: !!post.twelfth,
            minCGPA: !!post.minCGPA,
          });
        } catch (error) {
          console.error('Could not load job post:', error);
          navigate('/recruitment-dashboard');
        }
      } else {
        setFormData(initialFormData);
      }
    };
    fetchJobPostForEdit();
  }, [editId, navigate, isEdit]);

  const handleImportJD = (selectedOption) => {
    setSelectedSavedJD(selectedOption);
    if (!selectedOption) return;

    const jd = savedJDs.find((j) => j._id === selectedOption.value);
    if (!jd) return;

    let matchedWorkType = null;
    if (jd.hiring_type) {
      const typeStr = jd.hiring_type.toLowerCase();
      if (typeStr.includes('full')) matchedWorkType = workTypes.find((w) => w.value === 'Full-time');
      else if (typeStr.includes('intern')) matchedWorkType = workTypes.find((w) => w.value === 'Internship');
      else if (typeStr.includes('casual')) matchedWorkType = workTypes.find((w) => w.value === 'Casual');
    }

    let cleanedJobDescription = jd.generated_jd || '';
    
    if (jd.about_company && cleanedJobDescription.includes(jd.about_company)) {
      cleanedJobDescription = cleanedJobDescription.replace(jd.about_company, '');
    }
    
    cleanedJobDescription = cleanedJobDescription.replace(
      /(?:\*\*|#*)?\s*ABOUT THE COMPANY\s*(?:\*\*|#*)?[\s\S]*?(?=(?:\*\*|#*)?\s*ABOUT THE ROLE\s*(?:\*\*|#*)?|ROLES AND RESPONSIBILITIES)/i, 
      ''
    ).trim();

    // Auto-populate the Multi-Select array from AI
    let aiLocationObj = [];
    if (jd.location) {
        aiLocationObj = [{ label: jd.location, value: jd.location, country: null, __isNew__: true }];
        setSelectedLocations(aiLocationObj);
    }

    setFormData((prev) => ({
      ...prev,
      role: jd.job_role ? { value: jd.job_role, label: jd.job_role } : prev.role,
      location: jd.location || prev.location,
      ctc: jd.budget || prev.ctc,
      aboutCompany: jd.about_company || prev.aboutCompany,
      aboutJob: cleanedJobDescription || prev.aboutJob, 
      typeOfWork: matchedWorkType || prev.typeOfWork,
      additionalInfo: `Required Skills: ${jd.skills || 'N/A'}\nNotice Period: ${jd.notice_period || 'N/A'}`,
      selectedJd: jd.pdf_path || prev.selectedJd,
    }));

    alert(`Successfully autofilled details & attached PDF from "${jd.jd_name || jd.job_role}"!`);
  };

  const handleNavigateToGenerator = () => {
    if (onPostSuccess) {
      onPostSuccess('JD Generator');
    } else {
      navigate('/jd-generator');
    }
  };

  // 🚨 THE SAME-COUNTRY LOCKDOWN LOGIC 🚨
  const handleLocationChange = (newSelectedOptions) => {
    if (!newSelectedOptions || newSelectedOptions.length === 0) {
      setSelectedLocations([]);
      setFormData(prev => ({ ...prev, location: '' }));
      return;
    }

    // Filter out custom inputs like "Remote" that don't have an official country
    const strictLocations = newSelectedOptions.filter(opt => !opt.__isNew__ && opt.country);
    
    if (strictLocations.length > 0) {
      const lockedCountry = strictLocations[0].country;
      
      // Check if ANY of the selected API cities belong to a different country
      const hasMismatch = strictLocations.some(opt => opt.country !== lockedCountry);
      
      if (hasMismatch) {
        alert(`Hold up! You can only select cities within the same country (${lockedCountry}).`);
        return; // Block the state update
      }
    }

    setSelectedLocations(newSelectedOptions);
    // Combine them into a string for the backend (e.g. "Bengaluru, India | Mumbai, India | Remote")
    const combinedString = newSelectedOptions.map(opt => opt.value).join(' | ');
    setFormData(prev => ({ ...prev, location: combinedString }));
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSelectChange = (name, selectedOption) =>
    setFormData((prev) => ({ ...prev, [name]: selectedOption }));
  const handleFileChange = (e) =>
    setFormData((prev) => ({ ...prev, otherFiles: [...e.target.files] }));

  const handleJdFileChange = (e) => {
    setFormData((prev) => ({ ...prev, selectedJd: e.target.files[0] }));
    setSelectedSavedJD(null);
  };

  const handleCriteriaToggle = (criteria) => (e) => {
    const isChecked = e.target.checked;
    setEnableCriteria((prev) => ({ ...prev, [criteria]: isChecked }));
    if (!isChecked) {
      setFormData((prev) => ({ ...prev, [criteria]: '' }));
    }
  };

  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);

  const handlePostJob = async (selectedColleges, isPublic, isPremiumJob) => {
    if (!isEdit && selectedColleges.length === 0 && !isPublic && !isPremiumJob) {
      return alert('You must select at least one college OR check "Post to Careers Page".');
    }

    const dataToSend = new FormData();
    dataToSend.append('isPremiumJob', isPremiumJob);
    dataToSend.append('companyName', formData.companyName);

    if (formData.role) dataToSend.append('role', formData.role.value);

    dataToSend.append('batch', formData.batch);
    dataToSend.append('location', formData.location);
    dataToSend.append('ctc', formData.ctc);
    dataToSend.append('applyByDate', formData.applyByDate);
    dataToSend.append('applyByTime', formData.applyByTime);
    dataToSend.append('dateOfVisit', formData.dateOfVisit);

    if (enableCriteria.tenth) dataToSend.append('tenth', formData.tenth);
    if (enableCriteria.twelfth) dataToSend.append('twelfth', formData.twelfth);
    if (enableCriteria.minCGPA) dataToSend.append('minCGPA', formData.minCGPA);

    if (formData.graduation) {
      dataToSend.append('graduation', JSON.stringify(formData.graduation.map((g) => g.value)));
    }

    if (formData.typeOfWork) dataToSend.append('typeOfWork', formData.typeOfWork.value);
    if (formData.gender) dataToSend.append('gender', formData.gender.value);

    if (formData.targetCountry) {
      dataToSend.append('targetCountry', formData.targetCountry.value);
    }

    dataToSend.append('aboutCompany', formData.aboutCompany);
    dataToSend.append('aboutJob', formData.aboutJob);
    dataToSend.append('hiringWorkflows', formData.hiringWorkflows);
    dataToSend.append('externalLink', formData.externalLink);
    dataToSend.append('additionalInfo', formData.additionalInfo);

    const storedRecruiterId = localStorage.getItem('userId') || localStorage.getItem('recruiterId');
    const storedCustomerId = localStorage.getItem('customerId');

    const finalPosterId = providedId || storedRecruiterId || storedCustomerId;
    const finalPosterRole = providedId || storedCustomerId ? 'Customer' : 'Recruiter';

    if (!finalPosterId) {
      return alert("Error: We couldn't verify your session. Please log out and log back in.");
    }

    dataToSend.append('posterId', finalPosterId);
    dataToSend.append('posterRole', finalPosterRole);
    dataToSend.append('isPublicPost', isPublic);

    selectedColleges.forEach((collegeId) => {
      dataToSend.append('targetColleges[]', collegeId);
    });

    if (formData.selectedJd instanceof File) {
      dataToSend.append('jdDocument', formData.selectedJd);
    } else if (typeof formData.selectedJd === 'string') {
      dataToSend.append('jdDocument', formData.selectedJd);
    }

    if (formData.otherFiles && formData.otherFiles.length > 0) {
      Array.from(formData.otherFiles).forEach((file) => {
        dataToSend.append('otherFiles', file);
      });
    }

    try {
      if (isEdit) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${editId}`,
          dataToSend
        );
        alert('Job Updated Successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings`, dataToSend);
        alert('Job Posted Successfully!');
      }

      setFormData(initialFormData);
      setSelectedLocations([]);
      setStep(1);

      if (onPostSuccess) onPostSuccess('Posted Jobs');
      navigate(location.pathname);
    } catch (error) {
      console.error('Failed to save job post:', error);
      alert('Error: Could not save the job post. Check console.');
    }
  };

  if (step === 2) {
    return (
      <CollegeSelection
        formData={formData}
        onPostJob={handlePostJob}
        onBack={handleBack}
        // 🚨 CRITICAL FIX RESTORED: This hides the Premium logic for true Customers
        isRecruiter={!isCustomer}
      />
    );
  }

  return (
    <div className="jp-root">
      <div className="jp-bg-blob jp-blob-1" />
      <div className="jp-bg-blob jp-blob-2" />

      <div className="jp-header">
        <div className="jp-header-left">
          <div className="jp-title-icon">
            <Briefcase size={26} />
          </div>
          <div>
            <h1 className="jp-title">
              {isEdit ? 'Edit Job Posting' : 'Create New Job Posting'}
            </h1>
            <p className="jp-subtitle">
              {isEdit ? 'Update your job details' : 'Fill in the details to publish your opportunity'}
            </p>
          </div>
        </div>

        <div className="jp-stepper">
          <div className="jp-step active">
            <span className="jp-step-num">1</span>
            <span className="jp-step-label">Job Details</span>
          </div>
          <div className="jp-step-line" />
          <div className="jp-step">
            <span className="jp-step-num">2</span>
            <span className="jp-step-label">Select Colleges</span>
          </div>
        </div>
      </div>

      <div className="jp-layout">
        <div className="jp-form-col">

          <div className="jp-card jp-ai-card">
            <div className="jp-ai-header">
              <div className="jp-ai-icon">
                <Sparkles size={20} />
              </div>
              <div className="jp-ai-text">
                <h3>Autofill with AI Job Description</h3>
                <p>Select a previously generated JD to instantly populate this form and attach the PDF, or create a new one!</p>
              </div>
            </div>
            <div className="jp-ai-actions">
              <div className="jp-ai-select">
                <Select
                  options={savedJDs.map((jd) => ({
                    value: jd._id,
                    label: `${jd.jd_name || 'Unnamed JD'} - (${jd.job_role})`,
                  }))}
                  value={selectedSavedJD}
                  onChange={handleImportJD}
                  placeholder="Select a saved JD…"
                  classNamePrefix="react-select"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <button
                type="button"
                className="jp-generate-btn"
                onClick={handleNavigateToGenerator}
              >
                <Wand2 size={16} /> Generate New JD
              </button>
            </div>
          </div>

          <div className="jp-card">
            <div className="jp-section-header">
              <div className="jp-section-icon">
                <Building2 size={18} />
              </div>
              <h3>Primary Job Information</h3>
            </div>

            <div className="jp-form-grid">
              <div className="jp-field">
                <label>Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                />
              </div>

              <div className="jp-field">
                <label>Role / Designation</label>
                <CreatableSelect
                  isClearable
                  options={roleOptions}
                  value={formData.role}
                  onChange={(opt) => handleSelectChange('role', opt)}
                  classNamePrefix="react-select"
                  placeholder="Select or type…"
                />
              </div>

              {/* 🚨 DYNAMIC MULTI-CITY AUTOCOMPLETE 🚨 */}
              <div className="jp-field">
                <label>Location (Type City Name)</label>
                <AsyncCreatableSelect
                  isMulti
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCityOptions}
                  value={selectedLocations}
                  onChange={handleLocationChange}
                  classNamePrefix="react-select"
                  placeholder="e.g., Bengaluru..."
                  formatCreateLabel={(inputValue) => `Add Custom: "${inputValue}"`}
                />
              </div>

              <div className="jp-field">
                <label>Type of Work</label>
                <Select
                  options={workTypes}
                  value={formData.typeOfWork}
                  onChange={(opt) => handleSelectChange('typeOfWork', opt)}
                  classNamePrefix="react-select"
                  placeholder="Select type…"
                />
              </div>

              {formData.typeOfWork?.value === 'Internship' && (
                <div className="jp-field jp-fade-in">
                  <label>Target Batch (Internship Only)</label>
                  <input
                    type="text"
                    name="batch"
                    value={formData.batch}
                    onChange={handleChange}
                    placeholder="e.g., 2025"
                  />
                </div>
              )}

              <div className="jp-field">
                <label>Salary / Stipend</label>
                <input
                  type="text"
                  name="ctc"
                  value={formData.ctc}
                  onChange={handleChange}
                  placeholder="e.g., 12 LPA or 25k/month"
                />
              </div>

              <div className="jp-field">
                <label>Apply By Date</label>
                <input
                  type="date"
                  name="applyByDate"
                  value={formData.applyByDate}
                  onChange={handleChange}
                />
              </div>

              <div className="jp-field">
                <label>Apply By Time</label>
                <input
                  type="time"
                  name="applyByTime"
                  value={formData.applyByTime}
                  onChange={handleChange}
                />
              </div>

              <div className="jp-field">
                <label>Date of Visit (Optional)</label>
                <input
                  type="date"
                  name="dateOfVisit"
                  value={formData.dateOfVisit}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="jp-card">
            <div className="jp-section-header">
              <div className="jp-section-icon jp-icon-purple">
                <GraduationCap size={18} />
              </div>
              <h3>Education Details</h3>
            </div>

            <div className="jp-criteria-toggles">
              <label className="jp-checkbox">
                <input
                  type="checkbox"
                  checked={enableCriteria.tenth}
                  onChange={handleCriteriaToggle('tenth')}
                />
                <span>Set 10th % Criteria</span>
              </label>
              <label className="jp-checkbox">
                <input
                  type="checkbox"
                  checked={enableCriteria.twelfth}
                  onChange={handleCriteriaToggle('twelfth')}
                />
                <span>Set 12th % Criteria</span>
              </label>
              <label className="jp-checkbox">
                <input
                  type="checkbox"
                  checked={enableCriteria.minCGPA}
                  onChange={handleCriteriaToggle('minCGPA')}
                />
                <span>Set Min CGPA Criteria</span>
              </label>
            </div>

            <div className="jp-form-grid jp-grid-mt">
              {enableCriteria.tenth && (
                <div className="jp-field jp-fade-in">
                  <label>10th %</label>
                  <input
                    type="number"
                    name="tenth"
                    value={formData.tenth}
                    onChange={handleChange}
                    placeholder="e.g., 70"
                  />
                </div>
              )}

              {enableCriteria.twelfth && (
                <div className="jp-field jp-fade-in">
                  <label>12th %</label>
                  <input
                    type="number"
                    name="twelfth"
                    value={formData.twelfth}
                    onChange={handleChange}
                    placeholder="e.g., 75"
                  />
                </div>
              )}

              {enableCriteria.minCGPA && (
                <div className="jp-field jp-fade-in">
                  <label>Min. Current CGPA</label>
                  <input
                    type="number"
                    step="0.1"
                    name="minCGPA"
                    value={formData.minCGPA}
                    onChange={handleChange}
                    placeholder="e.g., 7.5"
                  />
                </div>
              )}

              <div className="jp-field jp-full">
                <label>Graduation Degree (Select or Type Multiple)</label>
                <CreatableSelect
                  isMulti
                  options={graduationDegrees}
                  value={formData.graduation}
                  onChange={(opt) => handleSelectChange('graduation', opt)}
                  classNamePrefix="react-select"
                  placeholder="Type to add custom degree…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="jp-field">
                <label>Gender</label>
                <Select
                  options={genders}
                  value={formData.gender}
                  onChange={(opt) => handleSelectChange('gender', opt)}
                  classNamePrefix="react-select"
                  placeholder="Select…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* 🚨 FIXED DYNAMIC LIVE COUNTRY AUTOCOMPLETE 🚨 */}
              <div className="jp-field">
                <label>Target Country (Select One)</label>
                <CreatableSelect
                  options={countryOptions}
                  value={formData.targetCountry}
                  onChange={(opt) => handleSelectChange('targetCountry', opt)}
                  classNamePrefix="react-select"
                  placeholder="Type to search country..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  formatCreateLabel={(inputValue) => `Select: "${inputValue}"`}
                />
              </div>
            </div>
          </div>

          <div className="jp-card">
            <div className="jp-section-header">
              <div className="jp-section-icon jp-icon-amber">
                <FileText size={18} />
              </div>
              <h3>Job & Company Details</h3>
            </div>

            <div className="jp-field jp-full">
              <label>About the Company</label>
              <textarea
                name="aboutCompany"
                value={formData.aboutCompany}
                onChange={handleChange}
                rows="5"
                placeholder="Describe your company…"
              />
            </div>

            <div className="jp-field jp-full">
              <label>About the Job</label>
              <textarea
                name="aboutJob"
                value={formData.aboutJob}
                onChange={handleChange}
                rows="7"
                placeholder="If you autofill an AI JD, the full description will populate here!"
              />
            </div>

            <div className="jp-field jp-full">
              <label>Additional Information (Optional)</label>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows="4"
                placeholder="Any other details candidates should know…"
              />
            </div>

            <div className="jp-field jp-full">
              <label>Hiring Workflow</label>
              <textarea
                name="hiringWorkflows"
                value={formData.hiringWorkflows}
                onChange={handleChange}
                rows="4"
                placeholder="e.g., Round 1: Online Test, Round 2: Technical Interview…"
              />
            </div>

            {!isCustomer && (
              <div className="jp-field jp-full">
                <label>External Company Link (Optional)</label>
                <input
                  type="text"
                  name="externalLink"
                  value={formData.externalLink}
                  onChange={handleChange}
                  placeholder="https://yourcompany.com/careers"
                />
              </div>
            )}
          </div>

          <div className="jp-card">
            <div className="jp-section-header">
              <div className="jp-section-icon jp-icon-rose">
                <Paperclip size={18} />
              </div>
              <h3>File Attachments</h3>
            </div>

            <div className="jp-field jp-full">
              <label>Job Description Document (PDF)</label>
              <div className="jp-file-row">
                <label className="jp-file-label">
                  <Upload size={14} /> Choose PDF
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleJdFileChange}
                    className="jp-file-hidden"
                  />
                </label>
                <span className="jp-file-name">
                  {formData.selectedJd instanceof File
                    ? formData.selectedJd.name
                    : typeof formData.selectedJd === 'string' && formData.selectedJd
                    ? 'AI PDF attached'
                    : 'No file chosen'}
                </span>
              </div>

              {typeof formData.selectedJd === 'string' && formData.selectedJd && (
                <span className="jp-success-msg">
                  <CheckCircle2 size={14} /> AI PDF Document Auto-Attached!
                </span>
              )}
            </div>

            <div className="jp-field jp-full">
              <label htmlFor="otherFiles">Upload Other Files (Optional)</label>
              <div className="jp-file-row">
                <label className="jp-file-label">
                  <Upload size={14} /> Choose Files
                  <input
                    id="otherFiles"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="jp-file-hidden"
                  />
                </label>
                <span className="jp-file-name">
                  {formData.otherFiles.length > 0
                    ? `${formData.otherFiles.length} file(s) selected`
                    : 'No file chosen'}
                </span>
              </div>
            </div>
          </div>

          <div className="jp-actions">
            <button type="button" className="jp-next-btn" onClick={handleNext}>
              Next: Select Colleges <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="jp-preview-col">
          <div className="jp-preview-sticky">
            <div className="jp-preview-header">
              <h4>Live Preview</h4>
              <span className="jp-preview-tag">Updates as you type</span>
            </div>
            <div className="jp-preview-content">
              <JobPostingPreview formData={formData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPosting;